import React, { useEffect, useState } from "react";

import coursePlanService from "../../services/v2/coursePlan.service";
import { CoursePlanBody, CoursePlanDTO } from "../../models/CoursePlanDTO";
import { getErrorMessage } from "../../common";
import { horarioBonito } from "../../utils/horarioBonito";
import { montoBonito } from "../../utils/monto";
import useEsTelefono from "../../utils/esTelefono";

import "../../navigation/v2/managment/enrollmentUpdates/styles.css";

/*
	Plan de pagos del curso (FASE 3, 2026-10-01): mensualidades, precio, asistencia mínima y precio de
	reposición, inscripción (precio de lista, con su valor por módulo; el horario la puede sobrescribir en su formulario); abajo,
	los horarios con lo que cobra cada uno. Vacío = hereda. Solo afecta matrículas nuevas.
	El Recargo es único: el de Cobros (Finanzas › Recargos); aquí no se edita.
*/

const S = {
	title: "Plan de Pagos",
	bySchedule: "Se cobra por horario",
	months: "Mensualidades",
	price: "Precio de mensualidad",
	sessions: "Sesiones",
	minAttendance: "Asistencia mínima %",
	minGrade: "Nota Mínima para Aprobar",
	general: "La general",
	recoveryPrice: "Precio de reposición",
	subscription: "Inscripción",
	noSubscription: "Sin precio",
	levels: "Inscripción por Módulo",
	inherits: "Hereda",
	noPlan: "Sin plan",
	edit: "Editar",
	back: "Volver",
	save: "Guardar",
	saved: "Plan guardado",
	schedule: "Horario",
	level: "Módulo",
	monthly: "Mensualidad",
	surcharge: "Sobrecargo",
	noSchedules: "Sin horarios por comenzar o activos.",
	source: { schedule: "horario", level: "módulo", course: "curso", campus: "plantel", none: "" } as { [k: string]: string }
};

interface Borrador { billableMonths: string; monthlyPrice: string; sessionsTotal: string; minAttendancePct: string; minGrade: string; recoverySessionPrice: string; subscriptionListPrice: string; modulos: { [id: number]: string }; }
type CampoNumerico = Exclude<keyof Borrador, "modulos">;

const texto = (v: number | null | undefined): string => (v === null || v === undefined ? "" : String(v));
const numeroONulo = (v: string): number | null => (v.trim() === "" ? null : Number(v));

const PlanDePagos: React.FC<{ campusId: number; courseId: number; puedeEditar: boolean }> = ({ campusId, courseId, puedeEditar }) => {
	const [data, setData] = useState<CoursePlanDTO | null>(null);
	const [borrador, setBorrador] = useState<Borrador | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [aviso, setAviso] = useState<{ text: string; ok: boolean } | null>(null);
	const movil = useEsTelefono();

	useEffect(() => {
		let vigente = true;
		setData(null);
		coursePlanService.getPlan(campusId, courseId)
			.then(r => vigente && setData(r.data))
			.catch(e => vigente && setAviso({ text: getErrorMessage(e), ok: false }));
		return () => { vigente = false; };
	}, [campusId, courseId]);

	if (!data) return aviso ? <div className="wau" style={{ padding: "0 15px" }}><div className="wau-aviso mal">{aviso.text}</div></div> : null;

	const m = (v: number | null | undefined): string => v === null || v === undefined ? "-" : `${data.currency.symbol}${montoBonito(v)} ${data.currency.code}`;
	const hereda = (v: number | null | undefined, pct?: boolean): string => `${S.inherits} ${v === null || v === undefined ? "-" : pct ? v + " %" : m(v)}`;
	const plan = data.plan;
	const fuenteTexto = (src: string): string => (src ? " (" + src + ")" : "");
	const heredaReposicion: string = hereda(data.campus.recoverySessionPrice) + fuenteTexto(data.campus.recoverySessionPrice != null ? S.source.campus : "");
	//Nota mínima (R2): vacío = la general del Portal de Clase (config lms.nota_minima), que el backend manda en effective.minGrade.
	const heredaNota: string = data.effective?.minGrade != null ? `${S.inherits} ${data.effective.minGrade}` : `${S.inherits} (${S.general.toLowerCase()})`;

	const editar = () => setBorrador({
		billableMonths: texto(plan?.billableMonths), monthlyPrice: texto(plan?.monthlyPrice), sessionsTotal: texto(plan?.sessionsTotal),
		minAttendancePct: texto(plan?.minAttendancePct), minGrade: texto(plan?.minGrade), recoverySessionPrice: texto(plan?.recoverySessionPrice),
		subscriptionListPrice: texto(plan?.subscriptionListPrice),
		modulos: Object.fromEntries((data.levels || []).map(l => [l.id, texto(l.subscriptionPriceOverride)]))
	});

	const guardar = async () => {
		if (!borrador) return;
		setBusy(true);
		setAviso(null);
		try {
			const body: CoursePlanBody = {
				billableMonths: Number(borrador.billableMonths), monthlyPrice: Number(borrador.monthlyPrice),
				sessionsTotal: numeroONulo(borrador.sessionsTotal),
				minAttendancePct: numeroONulo(borrador.minAttendancePct), minGrade: numeroONulo(borrador.minGrade), recoverySessionPrice: numeroONulo(borrador.recoverySessionPrice),
				subscriptionListPrice: numeroONulo(borrador.subscriptionListPrice), confirmed: true,
				// Solo los módulos que cambian (cada cambio pide permiso de precio y queda en Auditoría)
				levels: (data.levels || []).filter(l => numeroONulo(borrador.modulos[l.id] ?? "") !== l.subscriptionPriceOverride)
					.map(l => ({ id: l.id, subscriptionPriceOverride: numeroONulo(borrador.modulos[l.id] ?? "") }))
			};
			const r = await coursePlanService.savePlan(campusId, courseId, body);
			setData(r.data);
			setBorrador(null);
			setAviso({ text: S.saved, ok: true });
		} catch (e) {
			setAviso({ text: getErrorMessage(e), ok: false });
		} finally {
			setBusy(false);
		}
	};

	const campo = (llave: CampoNumerico, etiqueta: string, lectura: string, placeholder?: string, paso: number = 0.01) => (
		<label className="wau-campo" key={llave}><span className="wau-lab">{etiqueta}</span>
			{
				borrador
					? <input className="wau-entrada wau-num" type="number" min={0} step={paso} value={borrador[llave]} placeholder={placeholder}
						onChange={e => setBorrador({ ...borrador, [llave]: e.target.value })} style={{ textAlign: "right" }} aria-label={etiqueta} />
					: <b className="wau-num" style={{ fontSize: 15 }}>{lectura}</b>
			}
		</label>
	);

	const listo = borrador && Number(borrador.billableMonths) >= 1 && borrador.monthlyPrice !== "";

	return (
		<div className="wau" style={{ padding: "0 15px 15px 15px" }}>
			<div className="wau-caja">
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
					<h3 style={{ margin: 0 }}>{S.title}</h3>
					{data.scheduleBilled ? <span className="wau-pastilla acento">{S.bySchedule}</span> : null}
					{
						data.scheduleBilled || !puedeEditar ? null : borrador
							? <div className="wau-acciones" style={{ marginTop: 0 }}>
								<button type="button" className="wau-btn s mini" disabled={busy} onClick={() => setBorrador(null)}>{S.back}</button>
								<button type="button" className="wau-btn p mini" disabled={busy || !listo} onClick={guardar}>{S.save}</button>
							</div>
							: <button type="button" className="wau-btn s mini" onClick={editar}>{S.edit}</button>
					}
				</div>
				{aviso ? <div className={"wau-aviso " + (aviso.ok ? "ok" : "mal")} role="status"><span>{aviso.text}</span><button type="button" aria-label={S.back} onClick={() => setAviso(null)}>×</button></div> : null}
				{
					data.scheduleBilled ? null :
						<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 12 }}>
							{campo("billableMonths", S.months, plan ? String(plan.billableMonths) : S.noPlan, undefined, 1)}
							{campo("monthlyPrice", S.price, plan ? m(plan.monthlyPrice) : "-")}
							{campo("sessionsTotal", S.sessions, plan?.sessionsTotal ? String(plan.sessionsTotal) : "-", undefined, 1)}
							{campo("minAttendancePct", S.minAttendance, plan?.minAttendancePct != null ? plan.minAttendancePct + " %" : hereda(data.campus.minAttendancePct, true), hereda(data.campus.minAttendancePct, true), 1)}
							{campo("minGrade", S.minGrade, plan?.minGrade != null ? String(plan.minGrade) : heredaNota, heredaNota, 0.1)}
							{campo("recoverySessionPrice", S.recoveryPrice, plan?.recoverySessionPrice != null ? m(plan.recoverySessionPrice) : heredaReposicion, heredaReposicion)}
							{campo("subscriptionListPrice", S.subscription, plan?.subscriptionListPrice != null ? m(plan.subscriptionListPrice) : S.noSubscription, S.noSubscription)}
						</div>
				}
				{
					data.scheduleBilled || !(data.levels || []).length ? null :
						<div className="wau-tabla" style={{ marginBottom: 12 }}>
							<table>
								<thead><tr><th>{S.level}</th><th className="der">{S.levels}</th></tr></thead>
								<tbody className="wau-num">
									{data.levels.map(l => {
										const delCurso: string = plan?.subscriptionListPrice != null ? hereda(plan.subscriptionListPrice) + fuenteTexto(S.source.course) : S.noSubscription;
										return (
											<tr key={"l" + l.id}>
												<td>{l.name}</td>
												<td className="der">{
													borrador
														? <input className="wau-entrada wau-num" type="number" min={0} step={0.01} value={borrador.modulos[l.id] ?? ""} placeholder={delCurso} aria-label={S.subscription + " " + l.name}
															onChange={e => setBorrador({ ...borrador, modulos: { ...borrador.modulos, [l.id]: e.target.value } })} style={{ textAlign: "right", maxWidth: 200 }} />
														: <>{l.subscriptionPriceSource === "none" ? S.noSubscription : m(l.effectiveSubscriptionPrice)}{l.subscriptionPriceSource !== "none" ? <div className="wau-sec" style={{ fontSize: 12 }}>{S.source[l.subscriptionPriceSource]}</div> : null}</>
												}</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
				}
				{
					data.schedules.length === 0 ? <div className="wau-sec">{S.noSchedules}</div> : movil
						/* En teléfono la tabla de cinco columnas se partía a media palabra: cada horario pasa a una tarjeta. */
						? <div>
							{data.schedules.map(s => (
								<div key={s.id} className="wau-tarjeta">
									<div className="wau-num"><b>{horarioBonito(s.name)}</b></div>
									{s.levelName ? <div className="linea">{s.levelName}</div> : null}
									<div className="linea wau-num">{S.monthly} {m(s.effectiveMonthlyPrice)}</div>
									{s.monthlyPriceAdjustment ? <div className="linea wau-num">{S.surcharge} {(s.monthlyPriceAdjustment > 0 ? "+" : "") + montoBonito(s.monthlyPriceAdjustment)}</div> : null}
									<div className="linea wau-num">{S.recoveryPrice} {m(s.effectiveRecoverySessionPrice)}{S.source[s.recoverySessionPriceSource] ? " (" + S.source[s.recoverySessionPriceSource] + ")" : ""}</div>
									<div className="linea wau-num">{S.subscription} {s.subscriptionPriceSource === "none" ? S.noSubscription : m(s.effectiveSubscriptionPrice) + " (" + S.source[s.subscriptionPriceSource] + ")"}</div>
								</div>
							))}
						</div>
						: <div className="wau-tabla">
							<table>
								<thead><tr><th>{S.schedule}</th><th>{S.level}</th><th className="der">{S.monthly}</th><th className="der">{S.surcharge}</th><th className="der">{S.recoveryPrice}</th><th className="der">{S.subscription}</th></tr></thead>
								<tbody className="wau-num">
									{data.schedules.map(s => (
										<tr key={s.id}>
											<td>{horarioBonito(s.name)}</td>
											<td>{s.levelName || ""}</td>
											<td className="der">{m(s.effectiveMonthlyPrice)}</td>
											<td className="der">{s.monthlyPriceAdjustment ? (s.monthlyPriceAdjustment > 0 ? "+" : "") + montoBonito(s.monthlyPriceAdjustment) : "-"}</td>
											<td className="der">{m(s.effectiveRecoverySessionPrice)}{S.source[s.recoverySessionPriceSource] ? <div className="wau-sec" style={{ fontSize: 12 }}>{S.source[s.recoverySessionPriceSource]}</div> : null}</td>
											<td className="der">{s.subscriptionPriceSource === "none" ? "-" : m(s.effectiveSubscriptionPrice)}{S.source[s.subscriptionPriceSource] ? <div className="wau-sec" style={{ fontSize: 12 }}>{S.source[s.subscriptionPriceSource]}</div> : null}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
				}
			</div>
		</div>
	);
};

export default PlanDePagos;
